import React, { useState } from 'react';
import { AlertTriangle, Check, Copy, KeyRound, X } from 'lucide-react';
import toast from '../../design-system/toast';

import { Spinner } from '../../design-system';
import { CreatedApiKey } from '../../hooks/useOrgApiKeys';
import { ApiKeyScope } from '../../types/apiKey';
import {
  ALL_API_KEY_SCOPES,
  API_KEY_SCOPE_OPTIONS,
  summarizeApiKeyScopes,
} from '../../utils/apiKeyScopes';

type AccessMode = 'full' | 'read' | 'custom';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  createKey: (name: string, scopes: ApiKeyScope[]) => Promise<CreatedApiKey>;
  onCreated: () => void;
}

const accessModeButtonClass = (isActive: boolean) => (
  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
    isActive
      ? 'bg-brand-primary text-black shadow-sm'
      : 'text-ui-text-secondary hover:bg-ui-background-tertiary'
  }`
);

const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  isOpen,
  onClose,
  createKey,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [accessMode, setAccessMode] = useState<AccessMode>('full');
  const [selectedScopes, setSelectedScopes] = useState<ApiKeyScope[]>([...ALL_API_KEY_SCOPES]);

  const resetState = () => {
    setName('');
    setIsSubmitting(false);
    setError(null);
    setRevealed(null);
    setCopied(false);
    setAccessMode('full');
    setSelectedScopes([...ALL_API_KEY_SCOPES]);
  };

  const handleClose = () => {
    const wasRevealed = revealed !== null;
    resetState();
    onClose();
    if (wasRevealed) {
      onCreated();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    if (selectedScopes.length === 0) {
      setError('Select at least one scope');
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      const created = await createKey(trimmed, selectedScopes);
      setRevealed(created);
      toast.success('API key created — copy it now');
    } catch (err: any) {
      const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to create API key';
      setError(typeof message === 'string' ? message : 'Failed to create API key');
      toast.error(typeof message === 'string' ? message : 'Failed to create API key');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.plaintextKey);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy');
    }
  };

  const selectFullAccess = () => {
    setAccessMode('full');
    setSelectedScopes([...ALL_API_KEY_SCOPES]);
    setError(null);
  };

  const selectReadOnly = () => {
    setAccessMode('read');
    setSelectedScopes(['read']);
    setError(null);
  };

  const selectCustomAccess = () => {
    setAccessMode('custom');
    setError(null);
  };

  const toggleScope = (scope: ApiKeyScope) => {
    setAccessMode('custom');
    setError(null);
    setSelectedScopes((current) => (
      current.includes(scope)
        ? current.filter((selectedScope) => selectedScope !== scope)
        : [...current, scope]
    ));
  };

  if (!isOpen) return null;

  // Phase 2: reveal the plaintext key once
  if (revealed) {
    const scopeSummary = summarizeApiKeyScopes(revealed.apiKey.scopes);

    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="bg-ui-background-secondary rounded-lg shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-ui-border-primary">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                <h2 className="text-lg font-semibold text-ui-text-primary">
                  API Key Created
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-1 rounded hover:bg-ui-background-tertiary transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-ui-text-tertiary" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This key will not be shown again. Copy it now and store it somewhere safe.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                  {revealed.apiKey.name}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={revealed.plaintextKey}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-3 py-2 text-sm font-mono border border-ui-border-primary rounded-lg
                               bg-ui-background-tertiary text-ui-text-primary"
                  />
                  <button
                    onClick={handleCopy}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2
                               ${copied
                                 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                 : 'bg-brand-primary hover:bg-brand-primaryDark text-black'}`}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="mt-2 text-xs text-ui-text-tertiary">
                  Key prefix: <span className="font-mono">{revealed.apiKey.key_prefix}</span>
                </p>
                <p className="mt-1 text-xs text-ui-text-tertiary">
                  Access:{' '}
                  <span className="font-medium text-ui-text-secondary" title={scopeSummary.title}>
                    {scopeSummary.label}
                  </span>
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Phase 1: form
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-ui-background-secondary rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-ui-border-primary">
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode" />
              <h2 className="text-lg font-semibold text-ui-text-primary">
                Create API Key
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded hover:bg-ui-background-tertiary transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-ui-text-tertiary" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <p className="text-sm text-ui-text-secondary">
              API keys authenticate programmatic access to this organization via the public REST API.
            </p>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            <div>
              <label
                htmlFor="api-key-name"
                className="block text-sm font-medium text-ui-text-secondary mb-1"
              >
                Name
              </label>
              <input
                type="text"
                id="api-key-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="CI pipeline"
                maxLength={80}
                autoFocus
                disabled={isSubmitting}
                className="w-full px-4 py-2 border border-ui-border-primary rounded-lg
                           bg-ui-background-secondary text-ui-text-primary
                           placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                           focus:ring-2 focus:ring-brand-primary focus:border-transparent
                           disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-ui-text-tertiary">
                Give it a recognizable name like <span className="font-mono">CI pipeline</span> or <span className="font-mono">Terraform</span>.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="block text-sm font-medium text-ui-text-secondary">
                  Access
                </p>
                <span className="text-xs text-ui-text-tertiary">
                  {selectedScopes.length} {selectedScopes.length === 1 ? 'scope' : 'scopes'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1 rounded-lg border border-ui-border-primary bg-ui-background-tertiary p-1">
                <button
                  type="button"
                  aria-pressed={accessMode === 'full'}
                  onClick={selectFullAccess}
                  disabled={isSubmitting}
                  className={accessModeButtonClass(accessMode === 'full')}
                >
                  Full access
                </button>
                <button
                  type="button"
                  aria-pressed={accessMode === 'read'}
                  onClick={selectReadOnly}
                  disabled={isSubmitting}
                  className={accessModeButtonClass(accessMode === 'read')}
                >
                  Read-only
                </button>
                <button
                  type="button"
                  aria-pressed={accessMode === 'custom'}
                  onClick={selectCustomAccess}
                  disabled={isSubmitting}
                  className={accessModeButtonClass(accessMode === 'custom')}
                >
                  Custom
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {API_KEY_SCOPE_OPTIONS.map((option) => {
                  const checkboxId = `api-key-scope-${option.value.replace(':', '-')}`;
                  return (
                    <label
                      key={option.value}
                      htmlFor={checkboxId}
                      className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                        selectedScopes.includes(option.value)
                          ? 'border-brand-primary/60 bg-brand-primary/5 dark:bg-brand-primary/10'
                          : 'border-ui-border-primary hover:bg-ui-background-tertiary'
                      }`}
                    >
                      <input
                        id={checkboxId}
                        type="checkbox"
                        checked={selectedScopes.includes(option.value)}
                        onChange={() => toggleScope(option.value)}
                        disabled={isSubmitting}
                        className="mt-0.5 h-4 w-4 rounded border-ui-border-primary text-brand-primary focus:ring-brand-primary disabled:opacity-50"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ui-text-primary">
                          {option.label}
                        </span>
                        <span className="block text-xs text-ui-text-tertiary">
                          {option.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {selectedScopes.length === 0 && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  Select at least one scope.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-ui-text-secondary
                           hover:bg-ui-background-tertiary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !name.trim() || selectedScopes.length === 0}
                className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark
                           disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size={16} tone="current" />
                    Creating...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    Create Key
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateApiKeyModal;
